import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useNoticeDocumentGenerationStatusRetrieveSuspense } from "gen/endpoints/fund-family-event/fund-family-event";
import { NoticeDocumentGenerationStatusData } from "gen/model/noticeDocumentGenerationStatusData";
import { NoticeStatusEnum } from "gen/model/noticeStatusEnum";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { ErrorIcon } from "shared/icons/ErrorIcon";
import { WarningIcon } from "shared/icons/WarningIcon";
import { rootRoute } from "shared/navigation/Routes";

export interface CapitalEventFinalizeModalProps {
  fundFamilyId: string;
  fundFamilyEventId: string;

  onCloseComplete(): void;

  onFinish(): void;
}

export const CapitalEventFinalizeModal: React.FC<
  CapitalEventFinalizeModalProps
> = ({ fundFamilyId, fundFamilyEventId, onCloseComplete, onFinish }) => {
  const { t } = useTranslation(["capitalActivity", "common"]);
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const { data: noticeDocumentGenerationStatusData } =
    useNoticeDocumentGenerationStatusRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
    );
  const navigate = useNavigate();
  const noticesPath = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .capitalActivity({})
    .capitalActivityEvent({ fundFamilyEventId })
    .notices({}).$;

  const modalHeader = (data: NoticeDocumentGenerationStatusData) => {
    switch (data.status) {
      case NoticeStatusEnum.out_of_date:
      case NoticeStatusEnum.failed_generation:
        return t(
          "capitalActivity:capitalEventFinalizeModal.noticesFailedTitle",
        );
      default:
        return t("capitalActivity:capitalEventFinalizeModal.title");
    }
  };

  const modalBody = (data: NoticeDocumentGenerationStatusData) => {
    switch (data.status) {
      case NoticeStatusEnum.successful_generation:
        return (
          <ModalBody>
            <Text fontSize={14} fontWeight={400}>
              {t(
                "capitalActivity:capitalEventFinalizeModal.noticesCreatedDescription",
              )}
            </Text>
          </ModalBody>
        );
      case NoticeStatusEnum.not_started:
        return (
          <ModalBody>
            <Text fontSize={14} fontWeight={400}>
              {t(
                "capitalActivity:capitalEventFinalizeModal.noticesNotStartedDescription",
              )}
            </Text>
            <Alert marginTop="4" status="warning">
              <AlertIcon>
                <WarningIcon />
              </AlertIcon>
              <AlertDescription>
                {t(
                  "capitalActivity:capitalEventFinalizeModal.noticesNotStartedWarning",
                )}
              </AlertDescription>
            </Alert>
          </ModalBody>
        );
      default:
        return (
          <ModalBody>
            <Alert marginTop="4" status="error" mb="24px">
              <AlertIcon>
                <ErrorIcon />
              </AlertIcon>
              <AlertDescription>
                {t(
                  "capitalActivity:capitalEventFinalizeModal.noticesFailedError",
                )}
              </AlertDescription>
            </Alert>
          </ModalBody>
        );
    }
  };

  const modalConfirmButton = (data: NoticeDocumentGenerationStatusData) => {
    switch (data.status) {
      case NoticeStatusEnum.out_of_date:
      case NoticeStatusEnum.failed_generation:
        return (
          <Button colorScheme="secondary" onClick={() => navigate(noticesPath)}>
            {t("capitalActivity:capitalEventFinalizeModal.navNotices")}
          </Button>
        );
      default:
        return (
          <Button onClick={onFinish}>
            {t("capitalActivity:capitalEventFinalizeModal.submit")}
          </Button>
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {modalHeader(noticeDocumentGenerationStatusData)}
          <ModalCloseButton />
        </ModalHeader>
        {modalBody(noticeDocumentGenerationStatusData)}
        <ModalFooter gap={2}>
          <ButtonOrder
            dismiss={
              <Button colorScheme="secondary" onClick={onClose}>
                {t("common:cancel")}
              </Button>
            }
            confirm={modalConfirmButton(noticeDocumentGenerationStatusData)}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
